import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSearch } from "../lib/queries";
import { Icon, type IconName } from "./icons";
import { cx } from "./ui";

/**
 * One box over everything: organizations, apps, users, servers.
 *
 * An incident hands an operator a name, an email or a hostname — not a
 * category. Without this, finding it means guessing which list page owns that
 * kind of thing and searching again when the guess is wrong.
 *
 * Keyboard navigation is the feature rather than a refinement of it: something
 * opened with ⌘K and then finished with the mouse has not saved anybody
 * anything. The input keeps focus throughout and the highlighted row is tracked
 * in state, which is why the rows are not focusable themselves.
 */

interface Row {
  id: string;
  to: string;
  label: string;
  detail: string | null;
  group: string;
  icon: IconName;
  muted?: string;
}

const DEBOUNCE_MS = 200;

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const { data, isFetching, error } = useSearch(q);

  // ⌘K / Ctrl+K from anywhere. Bound to the window rather than a field so it
  // works wherever the operator happens to be. The rail's Search button raises
  // `palette:open` rather than lifting this state into Layout, which would make
  // every page re-render on a keystroke in here.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (open) {
          setOpen(false);
        } else {
          window.dispatchEvent(new CustomEvent("palette:open"));
        }
      }
    };
    const onRequest = () => {
      const anotherModalIsOpen = Array.from(document.querySelectorAll("dialog[open]")).some(
        (dialog) => dialog !== dialogRef.current,
      );
      if (anotherModalIsOpen) return;

      returnFocusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("palette:open", onRequest);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("palette:open", onRequest);
    };
  }, [open]);

  // Native modal behavior keeps background controls inert and keyboard focus in
  // the palette without competing hand-rolled traps.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) {
      const returnFocus = returnFocusRef.current;
      const frame = requestAnimationFrame(() => {
        if (returnFocus?.isConnected) returnFocus.focus();
        returnFocusRef.current = null;
      });
      return () => cancelAnimationFrame(frame);
    }

    setDraft("");
    setQ("");
    setActive(0);
    // After paint, or the element is not focusable yet.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Debounced so a four-table query does not run per keystroke.
  useEffect(() => {
    if (draft === q) return;
    const timer = setTimeout(() => {
      setQ(draft);
      setActive(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, q]);

  // Flattened once, so ↑/↓ can walk across group boundaries without caring
  // where they are.
  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    return [
      ...data.organizations.map((org) => ({
        id: `org-${org.id}`,
        to: `/organizations/${org.id}`,
        label: org.name,
        detail: org.slug,
        group: "Organizations",
        icon: "orgs" as IconName,
        muted: org.suspendedAt ? "suspended" : undefined,
      })),
      ...data.apps.map((app) => ({
        id: `app-${app.id}`,
        to: `/apps/${app.id}`,
        label: app.name,
        detail: app.organization.name,
        group: "Apps",
        icon: "apps" as IconName,
      })),
      ...data.users.map((user) => ({
        id: `user-${user.id}`,
        to: `/users/${user.id}`,
        label: user.name,
        detail: user.email,
        group: "Users",
        icon: "users" as IconName,
      })),
      ...data.servers.map((server) => ({
        id: `server-${server.id}`,
        to: `/servers/${server.id}`,
        label: server.name,
        detail: server.url,
        group: "Servers",
        icon: "servers" as IconName,
      })),
      // No page per domain; the list filtered to the name is the page.
      ...(data.domains ?? []).map((domain) => ({
        id: `domain-${domain.id}`,
        to: `/domains?q=${encodeURIComponent(domain.domain)}`,
        label: domain.domain,
        detail: domain.organization.name,
        group: "Domains",
        icon: "globe" as IconName,
        muted: domain.status !== "active" ? domain.status : undefined,
      })),
    ];
  }, [data]);

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function go(row: Row) {
    // Navigation gives the new page the next focus decision, not the control
    // that opened a search on the old one.
    returnFocusRef.current = null;
    setOpen(false);
    void navigate(row.to);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (rows.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((current) => (current + 1) % rows.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) => (current - 1 + rows.length) % rows.length);
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(rows.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = rows[active];
      if (row) go(row);
    }
  }

  const short = q.trim().length > 0 && q.trim().length < 2;
  let lastGroup: string | null = null;

  return (
    <dialog
      ref={dialogRef}
      onClose={() => setOpen(false)}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog itself.
        if (event.target === dialogRef.current) setOpen(false);
      }}
      aria-label="Search"
      className="m-auto max-h-[calc(100dvh-2rem)] w-[min(36rem,calc(100vw-2rem))] overflow-hidden border border-line bg-raised p-0 text-fg shadow-md backdrop:bg-canvas/70 backdrop:backdrop-blur-sm"
    >
      <div
        onKeyDown={onKeyDown}
        className="flex max-h-[calc(100dvh-2rem)] flex-col"
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Icon name="search" className="size-4 shrink-0 text-fg-subtle" />
          <input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Search organizations, apps, users, servers…"
            aria-label="Search organizations, apps, users and servers"
            aria-controls="palette-results"
            aria-activedescendant={rows[active] ? `palette-${rows[active].id}` : undefined}
            className="w-full bg-transparent py-3.5 text-sm pointer-coarse:text-base text-fg placeholder:text-fg-subtle focus:outline-none"
          />
          {isFetching && <Spinner />}
        </div>

        <div
          id="palette-results"
          ref={listRef}
          role="listbox"
          aria-label="Results"
          className="max-h-[min(20rem,calc(100dvh-11rem))] overflow-y-auto py-1"
        >
          {short ? (
            <Note>Keep typing — at least two characters.</Note>
          ) : error ? (
            <Note tone="error">{error.message}</Note>
          ) : q.trim().length < 2 ? (
            <Note>Find anything by name, email, slug or hostname.</Note>
          ) : rows.length === 0 && !isFetching ? (
            <Note>
              Nothing matches “{q}”.
            </Note>
          ) : (
            rows.map((row, index) => {
              const header = row.group !== lastGroup ? row.group : null;
              lastGroup = row.group;
              return (
                <div key={row.id}>
                  {header && <p className="micro px-4 pt-3 pb-1">{header}</p>}
                  <div
                    id={`palette-${row.id}`}
                    role="option"
                    aria-selected={index === active}
                    data-index={index}
                    onMouseMove={() => setActive(index)}
                    onClick={() => go(row)}
                    className={cx(
                      "flex cursor-pointer items-center gap-3 px-4 py-2 text-sm",
                      index === active ? "bg-surface text-fg" : "text-fg-muted",
                    )}
                  >
                    <Icon name={row.icon} className="size-4 shrink-0 text-fg-subtle" />
                    <span className="truncate font-medium text-fg">{row.label}</span>
                    {row.detail && <span className="truncate text-fg-subtle">{row.detail}</span>}
                    {row.muted && (
                      <span className="ml-auto shrink-0 text-xs text-warn">{row.muted}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-line px-4 py-2 text-xs text-fg-subtle">
          <Hint keys="↑↓">navigate</Hint>
          <Hint keys="↵">open</Hint>
          <Hint keys="esc">close</Hint>
        </div>
      </div>
    </dialog>
  );
}

function Note({ children, tone }: { children: React.ReactNode; tone?: "error" }) {
  return (
    <p className={cx("px-4 py-6 text-center text-sm", tone === "error" ? "text-danger" : "text-fg-subtle")}>
      {children}
    </p>
  );
}

function Hint({ keys, children }: { keys: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <kbd className="border border-line bg-surface px-1.5 py-0.5 font-mono text-[0.6875rem] text-fg-muted">
        {keys}
      </kbd>
      {children}
    </span>
  );
}

function Spinner() {
  return (
    <svg className="size-4 shrink-0 animate-spin text-fg-subtle" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}
